// ============================================================
// COMMERCIAL BANK LEDGER ENGINE - CORE PROTOTYPE
// ============================================================
// This is a working prototype of the general ledger described in
// the architecture document. It implements:
//
// 1. Double-entry bookkeeping with strict debit/credit balance enforcement
// 2. Immutable, append-only entry log (the event source)
// 3. Multi-currency support from day one
// 4. Materialised balance views with optimistic locking
// 5. Account lifecycle state machine
// 6. Transaction validation and atomicity
// 7. Hold/reservation system for pending transactions
// 8. Full audit trail on every operation
//
// In production, this would sit on PostgreSQL with proper ACID
// transactions. This prototype uses in-memory storage but the
// interfaces are designed for the real thing.
// ============================================================

import { businessDate as _defaultBusinessDate } from './business-date.js';

// ---- TYPES & ENUMS ----

const AccountType = {
  ASSET: 'ASSET',           // Things the bank owns (loans, cash, nostro)
  LIABILITY: 'LIABILITY',   // Things the bank owes (customer deposits)
  EQUITY: 'EQUITY',         // Shareholders' funds
  INCOME: 'INCOME',         // Revenue (interest income, fees)
  EXPENSE: 'EXPENSE',       // Costs (interest expense, opex)
};

const AccountStatus = {
  PENDING: 'PENDING',       // Created but KYC not complete
  ACTIVE: 'ACTIVE',         // Open and operational
  DORMANT: 'DORMANT',       // No activity for defined period
  FROZEN: 'FROZEN',         // Restricted by compliance
  CLOSING: 'CLOSING',       // In process of closing
  CLOSED: 'CLOSED',         // Permanently closed
};

const Direction = {
  DEBIT: 'DEBIT',
  CREDIT: 'CREDIT',
};

const EntryType = {
  NORMAL: 'NORMAL',
  REVERSAL: 'REVERSAL',
};

const HoldStatus = {
  ACTIVE: 'ACTIVE',
  RELEASED: 'RELEASED',
  EXPIRED: 'EXPIRED',
  CAPTURED: 'CAPTURED',     // Converted to actual entry
};

// Valid state transitions - enforced by the state machine
const VALID_TRANSITIONS = {
  [AccountStatus.PENDING]:  [AccountStatus.ACTIVE, AccountStatus.CLOSED],
  [AccountStatus.ACTIVE]:   [AccountStatus.DORMANT, AccountStatus.FROZEN, AccountStatus.CLOSING],
  [AccountStatus.DORMANT]:  [AccountStatus.ACTIVE, AccountStatus.FROZEN, AccountStatus.CLOSING],
  [AccountStatus.FROZEN]:   [AccountStatus.ACTIVE, AccountStatus.CLOSING],
  [AccountStatus.CLOSING]:  [AccountStatus.CLOSED],
  [AccountStatus.CLOSED]:   [], // Terminal state
};

// Whether debits increase or decrease the balance depends on account type
// Assets & Expenses: Debits increase, Credits decrease
// Liabilities, Equity & Income: Credits increase, Debits decrease
const DEBIT_NORMAL_TYPES = new Set([AccountType.ASSET, AccountType.EXPENSE]);

// ---- ID GENERATION ----

let _seqCounter = 0;
function generateId(prefix = '') {
  _seqCounter++;
  const ts = Date.now().toString(36);
  const seq = _seqCounter.toString(36).padStart(4, '0');
  const rand = Math.random().toString(36).substring(2, 6);
  return `${prefix}${ts}${seq}${rand}`;
}

// ---- EVENT BUS (simplified - Kafka in production) ----

class EventBus {
  constructor() {
    this.listeners = new Map();
    this.eventLog = []; // Append-only log
  }

  subscribe(eventType, handler) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(handler);
  }

  publish(event) {
    const enriched = {
      ...event,
      event_id: generateId('evt_'),
      timestamp: event._timestamp || new Date().toISOString(),
      version: 1,
    };
    this.eventLog.push(enriched); // Immutable append

    const handlers = this.listeners.get(event.type) || [];
    handlers.forEach(h => {
      try { h(enriched); }
      catch (e) { console.error(`Event handler error for ${event.type}:`, e); }
    });
    return enriched;
  }

  getEvents(filter = {}) {
    let events = [...this.eventLog];
    if (filter.type) events = events.filter(e => e.type === filter.type);
    if (filter.entityId) events = events.filter(e => e.entity_id === filter.entityId);
    if (filter.since) events = events.filter(e => e.timestamp > filter.since);
    return events;
  }
}

// ---- ACCOUNT ----

class Account {
  constructor({ id, accountNumber, name, type, currency, customerId = null, metadata = {}, _timestamp }) {
    this.id = id || generateId('acc_');
    this.accountNumber = accountNumber;
    this.name = name;
    this.type = type;
    this.currency = currency;
    this.status = AccountStatus.PENDING;
    this.customerId = customerId;
    this.metadata = metadata;
    this.openedAt = _timestamp || new Date().toISOString();
    this.closedAt = null;
    this.version = 0; // Optimistic locking
  }

  canTransition(newStatus) {
    return VALID_TRANSITIONS[this.status]?.includes(newStatus) || false;
  }

  isOperational() {
    return this.status === AccountStatus.ACTIVE;
  }
}

// ---- BALANCE ----
// Materialised view - can always be reconstructed from entries

class Balance {
  constructor(accountId, currency, _timestamp) {
    this.accountId = accountId;
    this.currency = currency;
    this.posted = 0;      // Settled balance from entries
    this.pending = 0;     // Posted + unreleased holds
    this.available = 0;   // Posted - active holds (what can actually be spent)
    this.version = 0;     // Optimistic locking
    this.lastUpdated = _timestamp || new Date().toISOString();
  }
}

// ---- ENTRY (Immutable) ----

class Entry {
  constructor({ transactionId, accountId, amount, direction, currency, effectiveDate, metadata = {}, _timestamp }) {
    this.id = generateId('ent_');
    this.transactionId = transactionId;
    this.accountId = accountId;
    this.amount = amount; // Always positive
    this.direction = direction;
    this.currency = currency;
    this.effectiveDate = effectiveDate || _timestamp || new Date().toISOString();
    this.postedAt = _timestamp || new Date().toISOString();
    this.metadata = metadata;
    this.entryType = EntryType.NORMAL;
    // Freeze - entries are IMMUTABLE
    Object.freeze(this);
  }
}

// ---- TRANSACTION ----

class Transaction {
  constructor({ id, type, reference, entries, metadata = {}, createdBy, _timestamp }) {
    this.id = id || generateId('txn_');
    this.type = type;
    this.reference = reference;
    this.entries = entries; // Array of Entry objects
    this.metadata = metadata;
    this.createdAt = _timestamp || new Date().toISOString();
    this.createdBy = createdBy || 'system';
    Object.freeze(this);
  }
}

// ---- HOLD ----

class Hold {
  constructor({ accountId, amount, currency, reason, expiresAt = null, metadata = {}, _timestamp }) {
    this.id = generateId('hld_');
    this.accountId = accountId;
    this.amount = amount;
    this.currency = currency;
    this.reason = reason;
    this.status = HoldStatus.ACTIVE;
    this.expiresAt = expiresAt;
    this.createdAt = _timestamp || new Date().toISOString();
    this.releasedAt = null;
    this.capturedTransactionId = null;
    this.metadata = metadata;
  }
}


// ============================================================
// THE LEDGER ENGINE
// ============================================================

class LedgerEngine {
  constructor(options) {
    this.accounts = new Map();       // accountId -> Account
    this.balances = new Map();       // accountId -> Balance
    this.entries = [];               // Append-only entry log
    this.transactions = new Map();   // transactionId -> Transaction
    this.holds = new Map();          // holdId -> Hold
    this.accruals = new Map();       // accountId -> accrual record (used by EOD)
    this.balanceSnapshots = [];      // Balance snapshots (used by EOD)
    this.reversedTransactionIds = new Set(); // PKB-002: prevent double-reversal

    // Accept optional EventBus, productLookup, and businessDate
    if (options && typeof options === 'object' && !(options instanceof EventBus)) {
      this.eventBus = options.eventBus || new EventBus();
      this.productLookup = options.productLookup || null;
      this._businessDate = options.businessDate || _defaultBusinessDate;
    } else if (options instanceof EventBus) {
      this.eventBus = options;
      this.productLookup = null;
      this._businessDate = _defaultBusinessDate;
    } else {
      this.eventBus = new EventBus();
      this.productLookup = null;
      this._businessDate = _defaultBusinessDate;
    }

    // Internal accounts (the bank's own chart of accounts)
    this._initializeChartOfAccounts();
  }

  _now() { return this._businessDate.getCurrentDate(); }
  _nowISO() { return this._now().toISOString(); }

  // ---- CHART OF ACCOUNTS ----
  // Every bank needs internal accounts for its own bookkeeping

  _initializeChartOfAccounts() {
    // Suspense / clearing accounts
    this.createAccount({
      accountNumber: 'INT-SUSPENSE-GBP',
      name: 'GBP Suspense Account',
      type: AccountType.LIABILITY,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Clearing and suspense' },
      autoActivate: true,
    });

    this.createAccount({
      accountNumber: 'INT-SUSPENSE-USD',
      name: 'USD Suspense Account',
      type: AccountType.LIABILITY,
      currency: 'USD',
      metadata: { internal: true, purpose: 'Clearing and suspense' },
      autoActivate: true,
    });

    this.createAccount({
      accountNumber: 'INT-SUSPENSE-EUR',
      name: 'EUR Suspense Account',
      type: AccountType.LIABILITY,
      currency: 'EUR',
      metadata: { internal: true, purpose: 'Clearing and suspense' },
      autoActivate: true,
    });

    // Income accounts
    this.createAccount({
      accountNumber: 'INT-INTEREST-INCOME',
      name: 'Interest Income',
      type: AccountType.INCOME,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Interest earned on loans' },
      autoActivate: true,
    });

    this.createAccount({
      accountNumber: 'INT-FEE-INCOME',
      name: 'Fee Income',
      type: AccountType.INCOME,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Service fees charged' },
      autoActivate: true,
    });

    // Expense accounts
    this.createAccount({
      accountNumber: 'INT-INTEREST-EXPENSE',
      name: 'Interest Expense',
      type: AccountType.EXPENSE,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Interest paid on deposits' },
      autoActivate: true,
    });

    // Loan interest receivable (lending engine accruals)
    this.createAccount({
      accountNumber: 'INT-LOAN-INTEREST-RECEIVABLE',
      name: 'Loan Interest Receivable',
      type: AccountType.ASSET,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Accrued interest receivable on loans' },
      autoActivate: true,
    });

    // Accrued interest payable (EOD deposit interest accruals)
    this.createAccount({
      accountNumber: 'INT-ACCRUED-INTEREST-PAYABLE',
      name: 'Accrued Interest Payable',
      type: AccountType.LIABILITY,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Accrued interest payable on deposits' },
      autoActivate: true,
    });

    // Accrued overdraft interest receivable (EOD engine)
    this.createAccount({
      accountNumber: 'INT-ACCRUED-OD-INTEREST',
      name: 'Accrued Overdraft Interest',
      type: AccountType.ASSET,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Accrued interest receivable on overdrafts' },
      autoActivate: true,
    });

    // ECL provision (lending engine IFRS 9)
    this.createAccount({
      accountNumber: 'INT-PROVISION-ECL',
      name: 'ECL Provision',
      type: AccountType.LIABILITY,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Expected credit loss provision under IFRS 9' },
      autoActivate: true,
    });

    // Impairment expense (lending engine ECL charge)
    this.createAccount({
      accountNumber: 'INT-IMPAIRMENT-EXPENSE',
      name: 'Impairment Expense',
      type: AccountType.EXPENSE,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Credit impairment charge to P&L' },
      autoActivate: true,
    });

    // Nostro account (our balance at the clearing bank)
    this.createAccount({
      accountNumber: 'INT-NOSTRO-GBP',
      name: 'GBP Nostro (Clearing Bank)',
      type: AccountType.ASSET,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Balance at clearing bank for FPS/BACS/CHAPS' },
      autoActivate: true,
    });

    // USD Nostro
    this.createAccount({
      accountNumber: 'INT-NOSTRO-USD',
      name: 'USD Nostro (Clearing Bank)',
      type: AccountType.ASSET,
      currency: 'USD',
      metadata: { internal: true, purpose: 'Balance at clearing bank for USD' },
      autoActivate: true,
    });

    // FX P&L
    this.createAccount({
      accountNumber: 'INT-FX-PNL',
      name: 'FX Gains/Losses',
      type: AccountType.INCOME,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Realised FX gains and losses' },
      autoActivate: true,
    });

    // Equity accounts
    this.createAccount({
      accountNumber: 'INT-RETAINED-EARNINGS',
      name: 'Retained Earnings',
      type: AccountType.EQUITY,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Accumulated profit/loss carried forward' },
      autoActivate: true,
    });

    this.createAccount({
      accountNumber: 'INT-SHARE-CAPITAL',
      name: 'Share Capital',
      type: AccountType.EQUITY,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Issued share capital' },
      autoActivate: true,
    });

    this.createAccount({
      accountNumber: 'INT-INVOICE-ADVANCES',
      name: 'Invoice Advances Outstanding',
      type: AccountType.ASSET,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Bank exposure from invoice advances' },
      autoActivate: true,
    });

    this.createAccount({
      accountNumber: 'INT-INVOICE-RETENTION',
      name: 'Invoice Retention Reserve',
      type: AccountType.LIABILITY,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Amount withheld pending debtor payment' },
      autoActivate: true,
    });

    this.createAccount({
      accountNumber: 'INT-INVOICE-SERVICE-FEE',
      name: 'Invoice Discounting Service Fee',
      type: AccountType.INCOME,
      currency: 'GBP',
      metadata: { internal: true, purpose: 'Service charge income from invoice discounting' },
      autoActivate: true,
    });
  }

  // ---- ACCOUNT MANAGEMENT ----

  createAccount({ accountNumber, name, type, currency, customerId, metadata, autoActivate }) {
    // Validate
    if (!Object.values(AccountType).includes(type)) {
      throw new Error(`Invalid account type: ${type}`);
    }
    if (!accountNumber || !name || !currency) {
      throw new Error('accountNumber, name, and currency are required');
    }

    // Check for duplicate account numbers
    for (const [, acc] of this.accounts) {
      if (acc.accountNumber === accountNumber) {
        throw new Error(`Account number ${accountNumber} already exists`);
      }
    }

    const account = new Account({ accountNumber, name, type, currency, customerId, metadata, _timestamp: this._nowISO() });
    this.accounts.set(account.id, account);

    // Initialize balance
    const balance = new Balance(account.id, currency, this._nowISO());
    this.balances.set(account.id, balance);

    this.eventBus.publish({
      type: 'AccountCreated',
      entity_id: account.id,
      data: {
        accountId: account.id,
        accountNumber,
        name,
        type,
        currency,
        customerId,
        status: account.status,
      }
    });

    // Auto-activate internal accounts
    if (autoActivate) {
      this.transitionAccountStatus(account.id, AccountStatus.ACTIVE, 'system', 'Internal account auto-activation');
    }

    return account;
  }

  transitionAccountStatus(accountId, newStatus, changedBy, reason) {
    const account = this.accounts.get(accountId);
    if (!account) throw new Error(`Account ${accountId} not found`);

    if (!account.canTransition(newStatus)) {
      throw new Error(`Cannot transition account ${accountId} from ${account.status} to ${newStatus}`);
    }

    // Business rules
    if (newStatus === AccountStatus.CLOSED) {
      const balance = this.getBalance(accountId);
      if (balance.posted !== 0) {
        throw new Error(`Cannot close account ${accountId}: balance is ${balance.posted} ${account.currency} (must be zero)`);
      }
      const activeHolds = this._getActiveHolds(accountId);
      if (activeHolds.length > 0) {
        throw new Error(`Cannot close account ${accountId}: ${activeHolds.length} active holds`);
      }
      account.closedAt = this._nowISO();
    }

    const oldStatus = account.status;
    account.status = newStatus;
    account.version++;

    this.eventBus.publish({
      type: 'AccountStatusChanged',
      entity_id: accountId,
      data: {
        accountId,
        previousStatus: oldStatus,
        newStatus,
        changedBy,
        reason,
      }
    });

    return account;
  }

  getAccount(accountId) {
    const account = this.accounts.get(accountId);
    if (!account) throw new Error(`Account ${accountId} not found`);
    return account;
  }

  getAccountByNumber(accountNumber) {
    for (const [, acc] of this.accounts) {
      if (acc.accountNumber === accountNumber) return acc;
    }
    throw new Error(`Account number ${accountNumber} not found`);
  }

  getAllAccounts(filter = {}) {
    let accounts = Array.from(this.accounts.values());
    if (filter.type) accounts = accounts.filter(a => a.type === filter.type);
    if (filter.currency) accounts = accounts.filter(a => a.currency === filter.currency);
    if (filter.status) accounts = accounts.filter(a => a.status === filter.status);
    if (filter.customerId) accounts = accounts.filter(a => a.customerId === filter.customerId);
    if (filter.internal === true) accounts = accounts.filter(a => a.metadata?.internal === true);
    if (filter.internal === false) accounts = accounts.filter(a => !a.metadata?.internal);
    return accounts;
  }

  // ---- BALANCE MANAGEMENT ----

  getBalance(accountId) {
    const balance = this.balances.get(accountId);
    if (!balance) throw new Error(`Balance not found for account ${accountId}`);
    return { ...balance }; // Return copy
  }

  _updateBalance(accountId, amount, direction) {
    const balance = this.balances.get(accountId);
    const account = this.accounts.get(accountId);
    if (!balance || !account) throw new Error(`Account or balance not found: ${accountId}`);

    const isDebitNormal = DEBIT_NORMAL_TYPES.has(account.type);
    let delta;

    if (direction === Direction.DEBIT) {
      delta = isDebitNormal ? amount : -amount;
    } else {
      delta = isDebitNormal ? -amount : amount;
    }

    balance.posted = Math.round((balance.posted + delta) * 100) / 100;
    balance.available = Math.round((balance.posted - this._totalActiveHolds(accountId)) * 100) / 100;
    balance.pending = balance.posted; // Simplified - in production, pending includes in-flight
    balance.version++;
    balance.lastUpdated = this._nowISO();
  }

  _totalActiveHolds(accountId) {
    return this._getActiveHolds(accountId)
      .reduce((sum, h) => sum + h.amount, 0);
  }

  _getActiveHolds(accountId) {
    return Array.from(this.holds.values())
      .filter(h => h.accountId === accountId && h.status === HoldStatus.ACTIVE);
  }

  // Rebuild balance from entries (proof that event sourcing works)
  reconstructBalance(accountId) {
    const account = this.accounts.get(accountId);
    if (!account) throw new Error(`Account ${accountId} not found`);

    const isDebitNormal = DEBIT_NORMAL_TYPES.has(account.type);
    let posted = 0;

    const accountEntries = this.entries.filter(e => e.accountId === accountId);
    for (const entry of accountEntries) {
      if (entry.direction === Direction.DEBIT) {
        posted += isDebitNormal ? entry.amount : -entry.amount;
      } else {
        posted += isDebitNormal ? -entry.amount : entry.amount;
      }
    }

    return Math.round(posted * 100) / 100;
  }

  // ---- TRANSACTION POSTING ----
  // This is the core of the ledger. Every financial event is a transaction
  // with balanced debit and credit entries.

  postTransaction({ type, reference, legs, metadata = {}, createdBy = 'system' }) {
    // Validate: legs must balance per currency
    this._validateLegs(legs);

    // Validate: all accounts exist and are operational
    for (const leg of legs) {
      const account = this.accounts.get(leg.accountId);
      if (!account) throw new Error(`Account ${leg.accountId} not found`);
      // Allow non-operational accounts for internal postings (e.g., suspense)
      if (!account.isOperational() && !account.metadata?.internal) {
        throw new Error(`Account ${leg.accountId} is not operational (status: ${account.status})`);
      }
      if (account.currency !== leg.currency) {
        throw new Error(`Currency mismatch: account ${leg.accountId} is ${account.currency}, entry is ${leg.currency}`);
      }
    }

    // Check available balance for debit legs on liability accounts (customer withdrawals)
    for (const leg of legs) {
      if (leg.direction === Direction.DEBIT) {
        const account = this.accounts.get(leg.accountId);
        // For customer deposit accounts (liability), a debit reduces the balance
        // Check if the customer has sufficient funds (including any overdraft facility)
        if (account.type === AccountType.LIABILITY && !account.metadata?.internal) {
          const balance = this.getBalance(leg.accountId);
          let effectiveAvailable = balance.available;
          if (this.productLookup) {
            const product = this.productLookup(account.metadata?.productId);
            if (product?.overdraft?.allowed) {
              const odLimit = (product.overdraft.maxCommitted || 0) + (product.overdraft.maxUncommitted || 0);
              effectiveAvailable = Math.round((balance.posted + odLimit - this._totalActiveHolds(leg.accountId)) * 100) / 100;
            }
          }
          if (effectiveAvailable < leg.amount) {
            throw new Error(
              `Insufficient funds in account ${account.accountNumber}: ` +
              `available ${balance.available} ${account.currency}, ` +
              `required ${leg.amount} ${account.currency}`
            );
          }
        }
      }
    }

    const transactionId = generateId('txn_');

    // Create immutable entries
    const nowISO = this._nowISO();
    const entries = legs.map(leg => new Entry({
      transactionId,
      accountId: leg.accountId,
      amount: leg.amount,
      direction: leg.direction,
      currency: leg.currency,
      effectiveDate: leg.effectiveDate,
      metadata: leg.metadata || {},
      _timestamp: nowISO,
    }));

    // Append entries to immutable log
    this.entries.push(...entries);

    // Update materialised balances
    for (const entry of entries) {
      this._updateBalance(entry.accountId, entry.amount, entry.direction);
    }

    // Create transaction record
    const transaction = new Transaction({
      id: transactionId,
      type,
      reference,
      entries,
      metadata,
      createdBy,
      _timestamp: nowISO,
    });
    this.transactions.set(transactionId, transaction);

    // Publish event
    this.eventBus.publish({
      type: 'TransactionPosted',
      entity_id: transactionId,
      data: {
        transactionId,
        transactionType: type,
        reference,
        entryCount: entries.length,
        entries: entries.map(e => ({
          accountId: e.accountId,
          amount: e.amount,
          direction: e.direction,
          currency: e.currency,
        })),
        createdBy,
      }
    });

    return transaction;
  }

  _validateLegs(legs) {
    if (!legs || legs.length < 2) {
      throw new Error('Transaction must have at least 2 legs (double-entry)');
    }

    // Group by currency and check balance
    const byCurrency = {};
    for (const leg of legs) {
      if (!leg.accountId || !leg.amount || !leg.direction || !leg.currency) {
        throw new Error('Each leg requires accountId, amount, direction, and currency');
      }
      if (leg.amount <= 0) {
        throw new Error('Entry amounts must be positive');
      }
      if (!Object.values(Direction).includes(leg.direction)) {
        throw new Error(`Invalid direction: ${leg.direction}`);
      }

      if (!byCurrency[leg.currency]) {
        byCurrency[leg.currency] = { debits: 0, credits: 0 };
      }
      if (leg.direction === Direction.DEBIT) {
        byCurrency[leg.currency].debits += leg.amount;
      } else {
        byCurrency[leg.currency].credits += leg.amount;
      }
    }

    // For single-currency transactions, debits must equal credits
    const currencies = Object.keys(byCurrency);
    if (currencies.length === 1) {
      const { debits, credits } = byCurrency[currencies[0]];
      const diff = Math.abs(debits - credits);
      if (diff > 0.001) { // Floating point tolerance
        throw new Error(
          `Transaction does not balance: debits=${debits}, credits=${credits} ` +
          `(difference: ${diff} ${currencies[0]})`
        );
      }
    }
    // Multi-currency transactions are allowed to not balance per currency
    // (FX transactions) but must have offsetting entries
  }

  // ---- REVERSALS ----
  // Corrections are always reversals, never modifications

  reverseTransaction(transactionId, reason, reversedBy = 'system') {
    const original = this.transactions.get(transactionId);
    if (!original) throw new Error(`Transaction ${transactionId} not found`);

    // PKB-002: Double-reversal guard
    if (this.reversedTransactionIds.has(transactionId)) {
      throw new Error('Transaction already reversed');
    }

    // Create reversal legs (swap debit/credit)
    const reversalLegs = original.entries.map(entry => ({
      accountId: entry.accountId,
      amount: entry.amount,
      direction: entry.direction === Direction.DEBIT ? Direction.CREDIT : Direction.DEBIT,
      currency: entry.currency,
      effectiveDate: this._nowISO(),
      metadata: {
        reversalOf: entry.id,
        reason,
      }
    }));

    const reversalTxn = this.postTransaction({
      type: 'REVERSAL',
      reference: `REV-${original.reference}`,
      legs: reversalLegs,
      metadata: {
        originalTransactionId: transactionId,
        reason,
      },
      createdBy: reversedBy,
    });

    // PKB-002: Record that this transaction has been reversed
    this.reversedTransactionIds.add(transactionId);

    this.eventBus.publish({
      type: 'TransactionReversed',
      entity_id: reversalTxn.id,
      data: {
        reversalTransactionId: reversalTxn.id,
        originalTransactionId: transactionId,
        reason,
        reversedBy,
      }
    });

    return reversalTxn;
  }

  // ---- HOLDS ----

  placeHold({ accountId, amount, currency, reason, expiresAt, metadata }) {
    const account = this.accounts.get(accountId);
    if (!account) throw new Error(`Account ${accountId} not found`);
    if (!account.isOperational()) throw new Error(`Account ${accountId} is not operational`);
    if (account.currency !== currency) throw new Error(`Currency mismatch`);

    const balance = this.getBalance(accountId);
    if (balance.available < amount) {
      throw new Error(`Insufficient available balance for hold: available ${balance.available}, requested ${amount}`);
    }

    const hold = new Hold({ accountId, amount, currency, reason, expiresAt, metadata, _timestamp: this._nowISO() });
    this.holds.set(hold.id, hold);

    // Update available balance
    const bal = this.balances.get(accountId);
    bal.available = Math.round((bal.posted - this._totalActiveHolds(accountId)) * 100) / 100;
    bal.version++;
    bal.lastUpdated = this._nowISO();

    this.eventBus.publish({
      type: 'HoldPlaced',
      entity_id: hold.id,
      data: { holdId: hold.id, accountId, amount, currency, reason }
    });

    return hold;
  }

  releaseHold(holdId) {
    const hold = this.holds.get(holdId);
    if (!hold) throw new Error(`Hold ${holdId} not found`);
    if (hold.status !== HoldStatus.ACTIVE) throw new Error(`Hold ${holdId} is not active`);

    hold.status = HoldStatus.RELEASED;
    hold.releasedAt = this._nowISO();

    // Update available balance
    const bal = this.balances.get(hold.accountId);
    bal.available = Math.round((bal.posted - this._totalActiveHolds(hold.accountId)) * 100) / 100;
    bal.version++;
    bal.lastUpdated = this._nowISO();

    this.eventBus.publish({
      type: 'HoldReleased',
      entity_id: holdId,
      data: { holdId, accountId: hold.accountId, amount: hold.amount }
    });

    return hold;
  }

  captureHold(holdId) {
    const hold = this.holds.get(holdId);
    if (!hold) throw new Error(`Hold ${holdId} not found`);
    if (hold.status !== HoldStatus.ACTIVE) throw new Error(`Hold ${holdId} is not active`);

    hold.status = HoldStatus.CAPTURED;
    hold.releasedAt = this._nowISO();

    // Update available balance (hold no longer counts against available)
    const bal = this.balances.get(hold.accountId);
    bal.available = Math.round((bal.posted - this._totalActiveHolds(hold.accountId)) * 100) / 100;
    bal.version++;
    bal.lastUpdated = this._nowISO();

    this.eventBus.publish({
      type: 'HoldCaptured',
      entity_id: holdId,
      data: { holdId, accountId: hold.accountId, amount: hold.amount }
    });

    return hold;
  }

  // ---- QUERIES ----

  getTransaction(transactionId) {
    return this.transactions.get(transactionId);
  }

  getEntriesForAccount(accountId, options = {}) {
    let entries = this.entries.filter(e => e.accountId === accountId);
    if (options.since) entries = entries.filter(e => e.postedAt >= options.since);
    if (options.until) entries = entries.filter(e => e.postedAt <= options.until);
    if (options.limit) entries = entries.slice(-options.limit);
    return entries;
  }

  getTransactionsForAccount(accountId, options = {}) {
    const entryTxnIds = new Set(
      this.entries.filter(e => e.accountId === accountId).map(e => e.transactionId)
    );
    let txns = Array.from(this.transactions.values())
      .filter(t => entryTxnIds.has(t.id));
    if (options.type) txns = txns.filter(t => t.type === options.type);
    if (options.limit) txns = txns.slice(-options.limit);
    return txns;
  }

  // ---- INTEGRITY CHECK ----
  // Run this periodically to verify the ledger is consistent

  verifyIntegrity() {
    const issues = [];

    // 1. Check every transaction balances (single currency)
    for (const [txnId, txn] of this.transactions) {
      const byCurrency = {};
      for (const entry of txn.entries) {
        if (!byCurrency[entry.currency]) byCurrency[entry.currency] = { debits: 0, credits: 0 };
        if (entry.direction === Direction.DEBIT) {
          byCurrency[entry.currency].debits += entry.amount;
        } else {
          byCurrency[entry.currency].credits += entry.amount;
        }
      }
      for (const [ccy, totals] of Object.entries(byCurrency)) {
        if (Math.abs(totals.debits - totals.credits) > 0.001) {
          issues.push(`Transaction ${txnId} does not balance in ${ccy}: D=${totals.debits} C=${totals.credits}`);
        }
      }
    }

    // 2. Check materialised balances match reconstructed balances
    for (const [accountId, balance] of this.balances) {
      const reconstructed = this.reconstructBalance(accountId);
      if (Math.abs(balance.posted - reconstructed) > 0.001) {
        issues.push(
          `Balance mismatch for ${accountId}: materialised=${balance.posted}, reconstructed=${reconstructed}`
        );
      }
    }

    // 3. Check total debits = total credits across ALL entries (per currency)
    const globalByCurrency = {};
    for (const entry of this.entries) {
      if (!globalByCurrency[entry.currency]) globalByCurrency[entry.currency] = { debits: 0, credits: 0 };
      if (entry.direction === Direction.DEBIT) {
        globalByCurrency[entry.currency].debits += entry.amount;
      } else {
        globalByCurrency[entry.currency].credits += entry.amount;
      }
    }
    for (const [ccy, totals] of Object.entries(globalByCurrency)) {
      if (Math.abs(totals.debits - totals.credits) > 0.001) {
        issues.push(`Global imbalance in ${ccy}: D=${totals.debits} C=${totals.credits}`);
      }
    }

    return {
      valid: issues.length === 0,
      issues,
      stats: {
        accounts: this.accounts.size,
        entries: this.entries.length,
        transactions: this.transactions.size,
        holds: this.holds.size,
      }
    };
  }

  // ---- REPORTING HELPERS ----

  getTrialBalance(currency = null) {
    const result = [];
    for (const [accountId, account] of this.accounts) {
      if (currency && account.currency !== currency) continue;
      const balance = this.getBalance(accountId);
      if (balance.posted !== 0) {
        result.push({
          accountId,
          accountNumber: account.accountNumber,
          name: account.name,
          type: account.type,
          currency: account.currency,
          balance: balance.posted,
          debitBalance: balance.posted > 0 && DEBIT_NORMAL_TYPES.has(account.type) ? balance.posted :
                        balance.posted < 0 && !DEBIT_NORMAL_TYPES.has(account.type) ? Math.abs(balance.posted) : 0,
          creditBalance: balance.posted > 0 && !DEBIT_NORMAL_TYPES.has(account.type) ? balance.posted :
                         balance.posted < 0 && DEBIT_NORMAL_TYPES.has(account.type) ? Math.abs(balance.posted) : 0,
        });
      }
    }
    return result;
  }

  // Convenience: get a summary suitable for display
  getSummary() {
    const accounts = this.getAllAccounts();
    const customerAccounts = accounts.filter(a => !a.metadata?.internal);
    const internalAccounts = accounts.filter(a => a.metadata?.internal);

    return {
      totalAccounts: accounts.length,
      customerAccounts: customerAccounts.length,
      internalAccounts: internalAccounts.length,
      totalTransactions: this.transactions.size,
      totalEntries: this.entries.length,
      activeHolds: Array.from(this.holds.values()).filter(h => h.status === HoldStatus.ACTIVE).length,
      integrity: this.verifyIntegrity(),
    };
  }
}


// ============================================================
// EXPORT FOR USE IN UI
// ============================================================
// In production these would be separate modules. For the prototype
// we expose everything the React UI needs.

export { LedgerEngine, AccountType, AccountStatus, Direction, EntryType, HoldStatus };
